import React, { useRef, ReactNode } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import { useStickyDetection } from 'hooks/use-sticky-detection';
import { typography, iconSizes, zIndex } from 'design-system/tokens';
import { Colors } from 'constants/colors';
import { useScrollSource } from './use-scroll-source';
import { useTabNavigation } from './use-tab-navigation';
import { useActiveTabTracking } from './use-active-tab-tracking';

const TAB_BAR_HEIGHT = 48;
const TAB_BAR_HEIGHT_WITH_ICONS = 64; // Taller when icons are visible

// Design system tokens for tab typography
// Active (sticky): body-md-bold (16px, 600, 1.4)
// Normal: body-md (16px, 500, 1.4)
const TAB_TYPOGRAPHY = {
  fontFamily: typography.fontFamily.manrope,
  fontSize: typography.fontSize.lg, // 16px
  lineHeight: typography.lineHeight.relaxed, // 1.4
  color: Colors.darkNavy, // #0C0F24 - primary-navy-900
  normal: {
    fontWeight: typography.fontWeight.medium, // 500
  },
  active: {
    fontWeight: typography.fontWeight.semibold, // 600
  },
};


export interface TabItem {
  id: string;
  label: string;
  icon?: ReactNode;
}

export interface StickyTabBarProps {
  tabs: TabItem[];
  onTabClick?: (tabId: string) => void;
  stickyTopOffset?: number;
  showIconsWhenSticky?: boolean;
  /** When true, disables sticky positioning (useful when parent is already sticky) */
  disableSticky?: boolean;
  /** Icon position relative to label: 'start' (left), 'top', 'end' (right), 'bottom' */
  iconPosition?: 'start' | 'top' | 'end' | 'bottom';
  /** Font size for tab labels (from design system typography tokens) */
  fontSize?: string | number;
  /** Icon size (pixel value, use design system iconSizes) */
  iconSize?: number;
  /** Viewport identifier for section ID prefixing (desktop/mobile) */
  viewport?: 'desktop' | 'mobile';
}

export function StickyTabBar({
  tabs,
  onTabClick,
  stickyTopOffset = 0,
  showIconsWhenSticky = false,
  disableSticky = false,
  iconPosition = 'start',
  fontSize = TAB_TYPOGRAPHY.fontSize,
  iconSize = parseInt(iconSizes.xxl),
  viewport = 'desktop',
}: StickyTabBarProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isSticky = useStickyDetection(sentinelRef);
  
  const source = useScrollSource();
  const shouldShowIcons = !isSticky || showIconsWhenSticky;
  const currentHeight = TAB_BAR_HEIGHT_WITH_ICONS;
  const scrollOffset = stickyTopOffset + currentHeight;
  
  const { scrollToTab, isScrollingRef } = useTabNavigation(viewport, source, scrollOffset);
  const { activeTab, setActiveTab } = useActiveTabTracking(tabs, viewport, stickyTopOffset, currentHeight, isScrollingRef, source);

  const handleChange = (_: React.SyntheticEvent, tabId: string) => {
    setActiveTab(tabId);
    scrollToTab(tabId);
    onTabClick?.(tabId);
  };

  return (
    <>
      <div ref={sentinelRef} aria-hidden="true" style={{ height: 0 }} />
      
      <Box
        sx={{
          ...(disableSticky ? {} : {
            position: 'sticky',
            top: stickyTopOffset,
            zIndex: zIndex.stickyTabBar,
          }),
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons='auto'
          sx={{
            minHeight: currentHeight,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontFamily: TAB_TYPOGRAPHY.fontFamily,
              fontSize: fontSize,
              lineHeight: TAB_TYPOGRAPHY.lineHeight,
              fontWeight: TAB_TYPOGRAPHY.normal.fontWeight,
              color: TAB_TYPOGRAPHY.color,
              minHeight: currentHeight,
              '&.Mui-selected': {
                fontWeight: TAB_TYPOGRAPHY.active.fontWeight,
                color: TAB_TYPOGRAPHY.color,
              },
            },
            '& .MuiTabScrollButton-root.Mui-disabled': {
              display: 'none',
            },
          }}
        >
          {tabs.map(tab => {
            // Conditionally render icon based on sticky state
            // No animations - just show/hide for performance
            const icon = tab.icon && shouldShowIcons ? (
              React.cloneElement(tab.icon as React.ReactElement, { 
                size: iconSize,
              })
            ) : undefined;
            
            return (
              <Tab
                key={tab.id}
                value={tab.id}
                label={tab.label}
                icon={icon}
                iconPosition={iconPosition}
              />
            );
          })}
        </Tabs>
      </Box>
    </>
  );
}

export { TAB_BAR_HEIGHT };
